import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Copy, Sparkles } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/invite-codes")({
  head: () => ({ meta: [{ title: "Invite codes — Admin" }] }),
  component: InviteCodes,
});

function InviteCodes() {
  const qc = useQueryClient();
  const [count, setCount] = useState(5);
  const [expiresIn, setExpiresIn] = useState<number | "">("");
  const [lastBatch, setLastBatch] = useState<string[]>([]);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ["invite-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invite_codes")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const generate = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("admin_generate_invite_codes", {
        _count: count,
        _expires_in_days: expiresIn === "" ? undefined : Number(expiresIn),
      });
      if (error) throw error;
      return (data ?? []) as string[];
    },
    onSuccess: (newCodes) => {
      setLastBatch(newCodes);
      toast.success(`${newCodes.length} code(s) generated`);
      qc.invalidateQueries({ queryKey: ["invite-codes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invite_codes").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Code deactivated");
      qc.invalidateQueries({ queryKey: ["invite-codes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  function copy(text: string) {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  }

  function statusOf(c: any): { label: string; variant: "default" | "outline" | "secondary" | "destructive" } {
    if (c.used_by) return { label: "Used", variant: "secondary" };
    if (!c.is_active) return { label: "Inactive", variant: "destructive" };
    if (c.expires_at && new Date(c.expires_at) < new Date()) return { label: "Expired", variant: "destructive" };
    return { label: "Active", variant: "default" };
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Invite codes</h2>
        <p className="text-sm text-muted-foreground">
          Generate one-time codes to send to new users. Each redeemed code grants the recipient a $10,000 welcome bonus and the $5,000 invest-to-unlock rule.
        </p>
      </div>

      <div className="glass rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> Generate codes</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Number of codes</Label>
            <Input type="number" min={1} max={100} value={count} onChange={(e) => setCount(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label>Expires in (days)</Label>
            <Input
              type="number"
              min={1}
              placeholder="Never"
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value === "" ? "" : Number(e.target.value))}
            />
          </div>
          <div className="flex items-end">
            <Button onClick={() => generate.mutate()} disabled={generate.isPending} className="w-full">
              {generate.isPending ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>

        {lastBatch.length > 0 && (
          <div className="rounded-lg bg-primary/10 border border-primary/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Just generated ({lastBatch.length})</div>
              <Button size="sm" variant="ghost" onClick={() => copy(lastBatch.map((c) => `${origin}/auth?ref=${c}`).join("\n"))}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy all links
              </Button>
            </div>
            <div className="space-y-1.5">
              {lastBatch.map((c) => (
                <div key={c} className="flex items-center justify-between gap-2 rounded bg-background/40 px-2 py-1.5 text-xs">
                  <span className="font-mono">{c}</span>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-muted-foreground truncate max-w-[260px]">{origin}/auth?ref={c}</span>
                    <Button size="sm" variant="ghost" onClick={() => copy(`${origin}/auth?ref=${c}`)}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="text-sm font-medium mb-3">All codes</div>
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No codes yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left border-b border-white/10">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Expires</th>
                  <th className="py-2 pr-3">Used at</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c: any) => {
                  const s = statusOf(c);
                  const canDeactivate = !c.used_by && c.is_active;
                  return (
                    <tr key={c.id} className="border-b border-white/5">
                      <td className="py-2 pr-3 font-mono">{c.code}</td>
                      <td className="py-2 pr-3"><Badge variant={s.variant} className="text-[10px]">{s.label}</Badge></td>
                      <td className="py-2 pr-3 text-xs">{c.expires_at ? fmtDate(c.expires_at) : "—"}</td>
                      <td className="py-2 pr-3 text-xs">{c.used_at ? fmtDate(c.used_at) : "—"}</td>
                      <td className="py-2 pr-3 text-xs">{fmtDate(c.created_at)}</td>
                      <td className="py-2 pr-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => copy(`${origin}/auth?ref=${c.code}`)} title="Copy link">
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          {canDeactivate && (
                            <Button size="sm" variant="ghost" onClick={() => deactivate.mutate(c.id)}>
                              Deactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}